import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export type CardFreshnessLabel = 'updated' | 'dataAsOf';

export interface ICardFreshnessStampProps {
  /** ISO 8601 instant the card's figures were calculated at. */
  calculatedAt: string;
  label?: CardFreshnessLabel;
  className?: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

const LABEL_KEYS: Record<CardFreshnessLabel, string> = {
  updated: 'Updated',
  dataAsOf: 'DataAsOf'
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;
const MINUTE_MS = 60000;

// The tick only has to be as fine as the unit the label prints: an hours-old stamp re-renders once
// an hour instead of 60 times for the same string, which matters because a fully granted tenant
// mounts one of these per card.
const tickIntervalFor = (elapsedMinutes: number): number => {
  if (elapsedMinutes < MINUTES_PER_HOUR) {
    return MINUTE_MS;
  }

  return elapsedMinutes < MINUTES_PER_DAY
    ? MINUTE_MS * MINUTES_PER_HOUR
    : MINUTE_MS * MINUTES_PER_DAY;
};

const Stamp = styled.time`
  display: block;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const elapsedPhrase = (minutes: number, t: Translate): string => {
  if (minutes < 1) {
    return t(`${TCustomerInsights}.Freshness.JustNow`);
  }

  if (minutes < MINUTES_PER_HOUR) {
    return t(`${TCustomerInsights}.Freshness.MinutesAgo`, { value: minutes });
  }

  if (minutes < MINUTES_PER_DAY) {
    return t(`${TCustomerInsights}.Freshness.HoursAgo`, {
      value: Math.floor(minutes / MINUTES_PER_HOUR)
    });
  }

  return t(`${TCustomerInsights}.Freshness.DaysAgo`, {
    value: Math.floor(minutes / MINUTES_PER_DAY)
  });
};

const CardFreshnessStamp: React.FC<ICardFreshnessStampProps> = ({
  calculatedAt,
  label = 'updated',
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  // The stamp states a relative age, so it has to keep re-deriving it: without a tick a tab left
  // open keeps claiming "1 min ago" for hours-old figures.
  const [now, setNow] = useState<number>(() => Date.now());

  // Parsed through moment in strict ISO mode rather than `new Date(...)`: Safari rejects a
  // date-only string (conventions/components/cross-browser.md § 3), and strict mode keeps an
  // unusable value out of the stamp instead of rendering "Invalid date".
  const stamp = useMemo(() => moment(calculatedAt, moment.ISO_8601, true), [calculatedAt]);
  const isValid = stamp.isValid();

  // Clock skew between browser and calculation service must not surface as a future stamp.
  const elapsedMinutes = isValid ? Math.max(0, moment(now).diff(stamp, 'minutes')) : 0;
  const tickInterval = tickIntervalFor(elapsedMinutes);

  useEffect(() => {
    // An unusable instant renders nothing, so it schedules nothing either.
    if (!isValid) {
      return undefined;
    }

    const tick = setInterval(() => setNow(Date.now()), tickInterval);

    return () => clearInterval(tick);
  }, [isValid, tickInterval]);

  if (!isValid) {
    return null;
  }

  return (
    <Stamp className={className} dateTime={calculatedAt} data-testid="card-freshness-stamp">
      {t(`${TCustomerInsights}.Freshness.${LABEL_KEYS[label]}`, {
        elapsed: elapsedPhrase(elapsedMinutes, t)
      })}
    </Stamp>
  );
};

export default CardFreshnessStamp;
